import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, Loader2, Paperclip } from 'lucide-react';
import { gamesAPI } from '../services/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAiModels, useAiSession } from '../hooks/use-ai-session';
import Seo from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import DashboardNavbar from '../components/DashboardNavbar';
import AttachmentButton from '../components/AttachmentButton';
import { ProgressDialog } from '../components/creator/ProgressDialog';
import { PreviewPane } from '../components/creator/PreviewPane';
import { ClassicCreator } from '../components/creator/ClassicCreator';
import { TilemapEditor } from '../components/creator/TilemapEditor';
import { DifficultyEditor } from '../components/creator/DifficultyEditor';

const GameCreator = () => {
  // Shared state
  const [prompt, setPrompt] = useState('');
  const [gameTitle, setGameTitle] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [aiMode, setAiMode] = useState('conversational'); // 'conversational' | 'classic'
  const [gameType, setGameType] = useState('freeform'); // 'freeform' | 'platformer2d'
  const [showTilemapEditor, setShowTilemapEditor] = useState(false);
  const [showDifficultyEditor, setShowDifficultyEditor] = useState(false);
  const [gameHtml, setGameHtml] = useState('');
  const [showProgress, setShowProgress] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [runtimeError, setRuntimeError] = useState(null);
  const [rollbackNotice, setRollbackNotice] = useState(false);
  const messagesEndRef = useRef(null);
  const lastGoodHtmlRef = useRef('');
  const currentHtmlRef = useRef('');
  const crashWatchRef = useRef(false);

  // AI models + session hooks
  const { models, loadModels } = useAiModels();
  const {
    session,
    loading: aiLoading,
    error: aiError,
    jobStatus,
    startSession,
    sendMessage,
    restoreVersion,
    commitToGame,
    loadSessionByGameId,
    loadSessionById,
    loadLatestSession,
    setSession,
    cancelCurrent,
  } = useAiSession();
  const [editingGameId, setEditingGameId] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, refreshUser } = useAuth();

  // Classic mode state
  const [genre, setGenre] = useState('arcade');
  const [style, setStyle] = useState('pixel art');
  const [difficulty, setDifficulty] = useState('medium');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [, setSessionId] = useState(null);

  useEffect(() => {
    const gameId = searchParams.get('gameId');
    const sessionIdParam = searchParams.get('sessionId');
    if (sessionIdParam) {
      // Opened directly into a session (e.g. via Remix)
      (async () => {
        try {
          const existing = await loadSessionById(sessionIdParam);
          setPrompt(existing.initial_prompt || '');
          setGameTitle(existing.title || '');
          setGameHtml(existing.current_code || '');
          setGameType(existing.metadata?.engine === 'platformer2d' ? 'platformer2d' : 'freeform');
        } catch (err) {
          console.warn('Could not load session from URL param');
        }
      })();
    } else if (gameId) {
      // Editing an existing game: try to load its AI session
      setEditingGameId(gameId);
      (async () => {
        try {
          const existingSession = await loadSessionByGameId(gameId);
          setPrompt(existingSession.initial_prompt || '');
          setGameTitle(existingSession.title || '');
          setGameHtml(existingSession.current_code || '');
          setGameType(existingSession.metadata?.engine === 'platformer2d' ? 'platformer2d' : 'freeform');
        } catch (err) {
          console.warn('No existing AI session for this game; starting new session on first run');
        }
      })();
    } else {
      // Auto-resume last session if user comes back to /create without a specific game
      (async () => {
        try {
          const latest = await loadLatestSession();
          setPrompt(latest.initial_prompt || '');
          setGameTitle(latest.title || '');
          setGameHtml(latest.current_code || '');
          setGameType(latest.metadata?.engine === 'platformer2d' ? 'platformer2d' : 'freeform');
        } catch (err) {
          // No previous sessions; ignore
        }
      })();
    }
  }, [loadSessionByGameId, loadSessionById, searchParams, editingGameId, loadLatestSession]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  // Default to the flagship model once the roster loads
  useEffect(() => {
    if (models.length > 0 && !selectedModelId) {
      setSelectedModelId(models[0].id);
    }
  }, [models, selectedModelId]);

  useEffect(() => {
    currentHtmlRef.current = gameHtml;
  }, [gameHtml]);

  useEffect(() => {
    if (session?.current_code) {
      setGameHtml(session.current_code);
    }
  }, [session]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [session]);

  // Listen for readiness + runtime errors from the iframe.
  // If a freshly generated build crashes on load, auto-rollback the preview
  // to the last known-good version.
  useEffect(() => {
    const handler = (event) => {
      if (!event.data) return;
      if (event.data.type === 'GAME_READY') {
        crashWatchRef.current = false;
        lastGoodHtmlRef.current = currentHtmlRef.current;
        setRuntimeError(null);
        return;
      }
      if (event.data.type === 'GAME_ERROR') {
        setRuntimeError(event.data);
        if (crashWatchRef.current && lastGoodHtmlRef.current) {
          crashWatchRef.current = false;
          setGameHtml(lastGoodHtmlRef.current);
          setRollbackNotice(true);
        }
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const handleAttachmentAdded = (attachment) => {
    setAttachments((prev) => [...prev, attachment]);
  };

  const handleCancel = () => {
    cancelCurrent();
    setShowProgress(false);
  };

  // Conversational AI handlers
  const handleStartAiSession = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!prompt.trim() || !selectedModelId) return;

    try {
      setShowProgress(true);
      const newSession = await startSession(
        prompt,
        selectedModelId,
        gameTitle.trim(),
        gameType === 'platformer2d' ? 'platformer2d' : null,
      );
      setGameHtml(newSession.current_code || '');
      setShowProgress(false);
      refreshUser();
    } catch (err) {
      if (!err.userCancelled) {
        console.error('Failed to start AI session', err);
      }
      setShowProgress(false);
    }
  };

  const sendChatMessage = async (message) => {
    if (!session || !message.trim() || aiLoading) return false;
    try {
      setShowProgress(true);
      const updated = await sendMessage(session.session_id, message);
      // Watch the new build for an immediate crash so we can auto-rollback
      setRollbackNotice(false);
      crashWatchRef.current = true;
      setTimeout(() => { crashWatchRef.current = false; }, 6000);
      setGameHtml(updated.current_code || '');
      setShowProgress(false);
      refreshUser();
      return true;
    } catch (err) {
      if (!err.userCancelled) {
        console.error('Failed to send chat', err);
      }
      setShowProgress(false);
      return false;
    }
  };

  const handleSendChat = async (e) => {
    e.preventDefault();
    if (!session || !chatInput.trim()) return;
    const message = chatInput;
    setChatInput('');
    const ok = await sendChatMessage(message);
    if (!ok) setChatInput(message);
  };

  const handleCommitGame = async () => {
    if (!session) return;
    try {
      const { game_id } = await commitToGame(session.session_id);
      if (!session.game_id || session.game_id !== game_id) {
        setSession({ ...session, game_id });
      }
      navigate(`/game/${game_id}`);
    } catch (err) {
      console.error('Failed to save game', err);
    }
  };

  // Classic one-shot generation helpers
  const pollGenerationStatus = async (sid) => {
    const interval = setInterval(async () => {
      try {
        const statusData = await gamesAPI.getGenerationStatus(sid);
        setProgress(statusData.progress);
        setStatus(statusData.status);

        if (statusData.status === 'completed') {
          clearInterval(interval);
          setGenerating(false);
          setTimeout(() => {
            navigate(`/game/${statusData.game_id}`);
          }, 1000);
        } else if (statusData.status === 'failed') {
          clearInterval(interval);
          setGenerating(false);
          setStatus('Generation failed. Please try again.');
        }
      } catch (error) {
        console.error('Error polling status:', error);
        clearInterval(interval);
        setGenerating(false);
      }
    }, 2000);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setGenerating(true);
    setProgress(0);
    setStatus('Starting generation...');

    try {
      const result = await gamesAPI.generateGame(prompt, genre, style, difficulty);
      setSessionId(result.session_id);
      setStatus('Generating your game...');
      pollGenerationStatus(result.session_id);
    } catch (error) {
      console.error('Error starting generation:', error);
      setGenerating(false);
      setStatus('Failed to start generation. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Seo
        title="AI Game Creator – GameOS"
        description="Describe your game idea and watch 55 AI models build it live — multi-level 2D platformers, cinematic 3D worlds and more."
        path="/create"
      />
      <DashboardNavbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {/* Hero */}
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              GameOS Agent
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {' '} Conversational Game Builder
              </span>
            </h1>
            <p className="text-gray-400 text-lg">
              Describe your game vibe, chat with the latest 2026 AI models, and iteratively build multi-level 2D and 3D HTML5 games.
            </p>
          </div>

          <Tabs value={aiMode} onValueChange={setAiMode} className="w-full">
            <TabsList className="bg-black/40 border border-white/10">
              <TabsTrigger value="conversational" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                Conversational Agent (Recommended)
              </TabsTrigger>
              <TabsTrigger value="classic" className="data-[state=active]:bg-gray-700 data-[state=active]:text-white">
                Classic One-Shot
              </TabsTrigger>
            </TabsList>

            <TabsContent value="conversational" className="mt-6">
              <div className="grid lg:grid-cols-2 gap-6 items-start">
                {/* Left: Model selection + chat */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10 space-y-4">
                    <div>
                      <Label className="text-white mb-2 block">AI Engine</Label>
                      <Select value={selectedModelId} onValueChange={setSelectedModelId}>
                        <SelectTrigger data-testid="ai-engine-select-trigger" className="bg-black/50 border-white/10 text-white">
                          <SelectValue placeholder={models.length ? 'Select a model' : 'Loading models...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {models.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {models.length > 0 && selectedModelId && (
                        <p className="mt-2 text-xs text-gray-400">
                          {models.find((m) => m.id === selectedModelId)?.description}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-white mb-2 block">Game Type</Label>
                      <Select value={gameType} onValueChange={setGameType}>
                        <SelectTrigger data-testid="game-type-select-trigger" className="bg-black/50 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="freeform">Freeform (AI writes all the code — 2D/3D)</SelectItem>
                          <SelectItem value="platformer2d">2D Platformer Engine (consistent quality + visual editors)</SelectItem>
                        </SelectContent>
                      </Select>
                      {gameType === 'platformer2d' && (
                        <p className="mt-2 text-xs text-gray-400">
                          The AI designs levels as data on top of a polished built-in engine. You get a tilemap editor and difficulty tuner after generation.
                        </p>
                      )}
                    </div>

                    <div className="grid gap-4">
                      <div>
                        <Label htmlFor="game-title" className="text-white text-sm mb-2 block">
                          Game Name (Title Screen)
                        </Label>
                        <input
                          id="game-title"
                          data-testid="game-title-input"
                          type="text"
                          value={gameTitle}
                          onChange={(e) => setGameTitle(e.target.value)}
                          placeholder="Example: Neon Runner: Cyber City Escape"
                          className="w-full rounded-md bg-black/50 border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/70"
                        />
                        <p className="mt-1 text-[11px] text-gray-400">
                          This name will appear on the in-game title screen with "Press SPACE to begin".
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="prompt" className="text-white text-sm mb-2 block">
                          Game Vibe / Initial Idea
                        </Label>
                        <Textarea
                          id="prompt"
                          data-testid="prompt-textarea"
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          placeholder="Example: Neon-soaked cyberpunk runner where you dodge drones and hack terminals to survive."
                          className="bg-black/50 border-white/10 text-white min-h-24 text-sm"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        data-testid="start-session-btn"
                        onClick={handleStartAiSession}
                        disabled={!prompt.trim() || !selectedModelId || !gameTitle.trim() || aiLoading}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      >
                        {aiLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Thinking with AI...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2" size={18} />
                            Start GameOS Session
                          </>
                        )}
                      </Button>
                      {aiLoading && (
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="inline-cancel-btn"
                          onClick={handleCancel}
                          className="border-red-500 text-red-400 hover:bg-red-500/10 text-xs px-3"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                    {aiError && (
                      <div className="mt-2 text-xs text-red-400 space-y-2">
                        <p>{aiError}</p>
                      </div>
                    )}
                  </div>

                  {/* Chat window */}
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10 flex flex-col h-[420px]">
                    <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                      {!session && (
                        <p className="text-gray-500 text-sm">
                          Start a session above to begin chatting with the GameOS agent.
                        </p>
                      )}
                      {session && session.messages?.map((msg) => (
                        <div
                          key={msg.id}
                          className={`p-3 rounded-lg text-sm max-w-[90%] ${
                            msg.role === 'user'
                              ? 'bg-purple-600/70 text-white ml-auto'
                              : msg.role === 'assistant'
                                ? 'bg-gray-800 text-gray-100'
                                : 'bg-black/40 text-gray-400 mx-auto text-xs'
                          }`}
                        >
                          {msg.content}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Suggested next builds (phased expansion) */}
                    {session && !aiLoading && session.metadata?.next_steps?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2" data-testid="next-steps-chips">
                        <span className="text-[11px] uppercase tracking-wide text-gray-500 w-full">
                          Suggested next builds — one fast increment at a time
                        </span>
                        {session.metadata.next_steps.map((step, i) => (
                          <button
                            key={i}
                            type="button"
                            data-testid={`next-step-chip-${i}`}
                            onClick={() => sendChatMessage(step)}
                            className="text-xs px-3 py-1.5 rounded-full border border-purple-500/40 bg-purple-500/10 text-purple-200 hover:bg-purple-500/25 transition-colors text-left"
                          >
                            + {step}
                          </button>
                        ))}
                      </div>
                    )}

                    <form onSubmit={handleSendChat} className="mt-4 flex gap-2 items-end">
                      <AttachmentButton sessionId={session?.session_id} onAttached={handleAttachmentAdded} />
                      <Textarea
                        data-testid="chat-input"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        placeholder={session ? 'Ask for changes: make enemies faster, add double jump, fix a bug...' : 'Start a session first.'}
                        className="flex-1 bg-black/50 border-white/10 text-white text-sm resize-none min-h-[44px] max-h-[80px]"
                        disabled={!session || aiLoading}
                      />
                      <Button
                        type="submit"
                        data-testid="send-chat-btn"
                        disabled={!session || !chatInput.trim() || aiLoading}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        Send
                      </Button>
                      {aiLoading && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancel}
                          className="border-red-500 text-red-400 hover:bg-red-500/10 text-xs px-3"
                        >
                          Cancel
                        </Button>
                      )}
                    </form>

                    {attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-gray-400">
                        {attachments.map((att) => (
                          <span
                            key={att.attachment_id}
                            className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/40 px-2 py-0.5"
                          >
                            <Paperclip className="h-3 w-3" />
                            <span className="truncate max-w-[120px]">{att.filename}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Live preview + versions + commit */}
                <div className="space-y-4">
                  {session?.metadata?.engine === 'platformer2d' && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="open-tilemap-editor-btn"
                        onClick={() => setShowTilemapEditor(true)}
                        className="border-purple-500 text-purple-300 hover:bg-purple-500/10"
                      >
                        Level Editor
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="open-difficulty-editor-btn"
                        onClick={() => setShowDifficultyEditor(true)}
                        className="border-purple-500 text-purple-300 hover:bg-purple-500/10"
                      >
                        Difficulty Tuner
                      </Button>
                    </div>
                  )}
                  <PreviewPane
                    gameHtml={gameHtml}
                    session={session}
                    runtimeError={runtimeError}
                    rollbackNotice={rollbackNotice}
                    onCommit={handleCommitGame}
                    onRestore={(versionId) => restoreVersion(session.session_id, versionId)}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="classic" className="mt-6">
              <ClassicCreator
                prompt={prompt}
                setPrompt={setPrompt}
                genre={genre}
                setGenre={setGenre}
                style={style}
                setStyle={setStyle}
                difficulty={difficulty}
                setDifficulty={setDifficulty}
                generating={generating}
                progress={progress}
                status={status}
                onGenerate={handleGenerate}
              />
            </TabsContent>
          </Tabs>

          <ProgressDialog
            open={showProgress}
            jobStatus={jobStatus}
            onCancel={handleCancel}
          />

          <TilemapEditor
            open={showTilemapEditor}
            onClose={() => setShowTilemapEditor(false)}
            session={session}
            onSaved={(updated) => {
              setSession(updated);
              setGameHtml(updated.current_code || '');
            }}
          />
          <DifficultyEditor
            open={showDifficultyEditor}
            onClose={() => setShowDifficultyEditor(false)}
            session={session}
            onSaved={(updated) => {
              setSession(updated);
              setGameHtml(updated.current_code || '');
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default GameCreator;
